import { all, takeLatest, put, call } from 'redux-saga/effects';
import {
  GLOBAL_INIT,
  UPDATE_AUTHORITY,
  UPDATE_AD_ACCOUNT_INFO,
  UPDATE_CURRENCY,
  UPDATE_CURRENCY_CONFIG,
  UPDATE_FUND_IDS,
  UPDATE_TIMEZONE,
  GET_CONFIG_DICTS,
} from 'store';
import { AdAccountInfoVO, AdAccountVO, IAction } from 'models/global';
import { getUserInfo } from 'service/auth';
import { initLogger } from 'utils/logger';
import * as Sentry from '@sentry/react';
import { customLocalStorage } from 'utils/storage';
import { postRequest } from '../utils/request';
import { ADVERTISER_DETAIL_URL } from '../constants/serviceUrl';
import { setUtcOffset } from '../utils/moment';
import { FUND_IDS_ENUM, Resource, ResponseCode, TIMEZONE } from '../constants/global';
import { getUrlParams } from '../utils/url';

export const ROOT_INIT = 'saga/root/init';

export default function* rootWatcher() {
  yield all([takeLatest(ROOT_INIT, init)]);
}

function* init(action: IAction) {
  try {
    // 有登录状态却没有商务中心id或广告账户id，则返回入口页进行选择
    const orgId = getUrlParams('orgId');
    const accountId = getUrlParams('accountId');
    if (!orgId || !accountId) {
      window.location.href = '/entrance';
      return;
    }

    yield put({
      type: GET_CONFIG_DICTS,
    });
    // 上一步 GET_CONFIG_DICTS 没拿到数据并不会阻断，因为内部进行了 try catch
    // 就会导致 res 为空，拿不到用户信息，此时直接返回，不进入后续逻辑
    const res: any = yield call(getUserInfo, { complete: true });
    if (!res?.data) {
      return;
    }

    const { data } = res;
    if (data.code !== ResponseCode.SUCCESS) {
      window.location.href = '/403';
      return;
    }

    const { user, user_country: country } = data.result;
    // bigo-logger日志上报
    initLogger(user.userId, country);
    // 一般来说国家码不会变，对于还没拿到接口国家码的，使用本地存储的国家码
    customLocalStorage.setItem('DSP_COUNTRY_CODE', country);

    Sentry.configureScope((scope) => {
      scope.setUser({ id: user?.userId, name: user?.name, email: user?.email, language: user?.language });
    });

    // setLanguage(user.language);
    yield put({
      type: GLOBAL_INIT,
      payload: user,
    });

    // @ts-ignore 在页面刷新的请求将自动在请求头中传递该用户id
    window.DSP_USER_ID = user.userId || '';
    window.DSP_USER_COUNTRY = user.country_code || '';

    const accountInfo: AdAccountInfoVO = yield postRequest(ADVERTISER_DETAIL_URL);

    // 如果用户无系统登录权限，则在 GET_CONFIG_DICTS 时就已经因为 401 被重定向到登录页面了
    // 此时仍然拿不到账户数据，则可能是对应账户无权限（400005）、对应账户不存在（40006）、对应组织不存在（400002）等等，去往选择页面重新选择
    if (!accountInfo) {
      window.location.href = '/entrance';
      return;
    }

    let permissionList = accountInfo.permissionList || [];
    const fundIds = accountInfo.fund_ids || [];

    // 用户无权限
    if (!permissionList.length) {
      window.location.href = '/403';
      return;
    }

    if (fundIds.includes(FUND_IDS_ENUM.dsp_adAc_pixel)) {
      permissionList.push(Resource.Pixel);
    }

    if (fundIds.includes(FUND_IDS_ENUM.dsp_adAc_material)) {
      permissionList.push(Resource.Material);
    }

    if (fundIds.includes(FUND_IDS_ENUM.dsp_adAc_custom_audience)) {
      permissionList.push(Resource.Target);
    }

    if (fundIds.includes(FUND_IDS_ENUM.series_goods)) {
      permissionList.push(Resource.Goods);
    }

    if (fundIds.includes(FUND_IDS_ENUM.instant_form)) {
      permissionList.push(Resource.Form);
    }

    if (fundIds.includes(FUND_IDS_ENUM.playable_transform)) {
      permissionList.push(Resource.Tools);
    }

    // 设置广告账户信息
    yield put({
      type: UPDATE_AD_ACCOUNT_INFO,
      payload: accountInfo.adAccount,
    });

    // 设置当前时区偏移量
    setUtcOffset(accountInfo.adAccount.timezone);

    // 设置当前时区名称
    yield put({
      type: UPDATE_TIMEZONE,
      payload: getTimezone(accountInfo.adAccount),
    });

    // 设置当前币种信息
    yield put({
      type: UPDATE_CURRENCY,
      payload: accountInfo.adAccount.currency,
    });

    const otherCurrencyConfigs = {
      INR: { step: 0.1, precision: 1 },
      USD: { step: 0.01, precision: 2 },
    };

    yield put({
      type: UPDATE_CURRENCY_CONFIG,
      payload: {
        ...accountInfo.currency[accountInfo.adAccount.currency],
        ...otherCurrencyConfigs[accountInfo.adAccount.currency],
      },
    });

    const noCurrentyConf = !accountInfo?.currency[accountInfo?.adAccount?.currency];
    if (noCurrentyConf) {
      console.error('获取用户 currency 配置失败！');
    }

    // 部分权限点依赖于白名单，故需对这部分权限进行过滤
    const fundIdPermissionMap = {
      [FUND_IDS_ENUM.conversion_no_pixel]: [Resource.WebPageRead, Resource.WebPageEdit],
      [FUND_IDS_ENUM.series_goods]: [Resource.GoodsRead, Resource.GoodsEdit],
    };
    Object.entries(fundIdPermissionMap).forEach(([key, value]) => {
      if (!fundIds.includes(key)) {
        permissionList = permissionList.filter((item) => !value.includes(item));
      }
    });

    yield put({
      type: UPDATE_AUTHORITY,
      payload: permissionList,
    });

    yield put({
      type: UPDATE_FUND_IDS,
      payload: fundIds,
    });

    yield put({
      type: GET_CONFIG_DICTS,
    });

    action.payload();
  } catch (e) {
    Sentry.captureException(e);
    if (e.response?.status === 500) {
      window.location.href = '/500';
    }
  }
}

// 获取当前时区名称
function getTimezone(adAccountInfo: AdAccountVO) {
  const tz = TIMEZONE.find((item) => item.value === adAccountInfo.timezone);
  if (!tz) {
    return '';
  }
  const res = /\((.+)\)/.exec(tz.label);
  return res ? res[1] : '';
}
